import { useEffect, useRef, useState } from 'react';
import { submitOfflineDonation } from '../api';
import { readUtmParams } from '../utmParams';
import { COUNTRIES_WITH_STATE } from '../intlCountries';
import { useEnglishTermsDialog, TermsLink } from './ConsentTerms';

// "Donate by Check, Wire Transfer or DAF" card, copied from the English donate
// page on the Wix site. Collects the donor's details and hands them to the
// backend, which forwards them to the client's webhooks — those email the donor
// the instructions. Nothing is charged here.

const EMPTY = { firstName: '', lastName: '', country: '', state: '', phone: '', email: '' };

// Check / wire / DAF donations are only taken in these regions. `code` feeds the
// state field (COUNTRIES_WITH_STATE); Europe is a region, so it has none.
const REGIONS = [
  { name: 'Europe', code: null },
  { name: 'Australia', code: 'AU' },
  { name: 'Canada', code: 'CA' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'United States', code: 'US' },
];

function validate(form) {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = 'Please enter your first name';
  if (!form.lastName.trim()) errors.lastName = 'Please enter your last name';
  if (!form.country) errors.country = 'Please select a country';
  if (!form.email.trim()) errors.email = 'Please enter your email';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Please enter a valid email';
  return errors;
}

export default function OfflineDonationForm({ onOnlineForm }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent
  const [sendError, setSendError] = useState('');
  const sentRef = useRef(null);
  const [openTerms, termsDialog] = useEnglishTermsDialog();

  // The thank-you is much shorter than the form it replaces, so a donor who
  // scrolled down to the button would otherwise be left looking past it.
  useEffect(() => {
    if (status === 'sent' && sentRef.current && sentRef.current.scrollIntoView) {
      sentRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [status]);

  const countryCode = REGIONS.find((r) => r.name === form.country)?.code;
  const askState = COUNTRIES_WITH_STATE.has(countryCode);

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((errs) => ({ ...errs, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setStatus('sending');
    setSendError('');
    const result = await submitOfflineDonation({
      ...form,
      state: askState ? form.state : '',
      ...readUtmParams(),
    });
    if (result.ok) {
      setStatus('sent');
    } else {
      setStatus('idle');
      setSendError(result.error);
    }
  };

  if (status === 'sent') {
    return (
      <div className="odf odf--sent" role="status" ref={sentRef}>
        <h2 className="odf-title">Thank you!</h2>
        <p className="odf-intro">
          We&apos;ve received your details and will email the instructions to {form.email.trim()} shortly.
        </p>
      </div>
    );
  }

  const field = (key, label, input, required) => (
    <label className={`odf-field${errors[key] ? ' has-error' : ''}`}>
      <span className="odf-label">{label}{required && ' *'}</span>
      {input}
      {errors[key] && <span className="odf-error">{errors[key]}</span>}
    </label>
  );

  return (
    <form className="odf" onSubmit={onSubmit} noValidate>
      <h2 className="odf-title">Donate by Check, Wire Transfer or DAF</h2>
      <p className="odf-intro">
        We receive tax-deductible donations via check, wire transfer and donor-advised funds in the US,
        Canada, UK, Europe, and Australia. Please fill out this form and we&apos;ll send you detailed
        instructions.
      </p>

      <div className="odf-row">
        {field('firstName', 'First Name',
          <input type="text" autoComplete="given-name" value={form.firstName} onChange={setField('firstName')} />, true)}
        {field('lastName', 'Last Name',
          <input type="text" autoComplete="family-name" value={form.lastName} onChange={setField('lastName')} />, true)}
      </div>

      {field('country', 'Country',
        <select value={form.country} onChange={setField('country')} autoComplete="country-name">
          <option value="">Select a Country</option>
          {REGIONS.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
        </select>, true)}
      <p className="odf-region-note">
        If your country does not appear on the list you can donate via the{' '}
        <button type="button" className="odf-link" onClick={onOnlineForm}>online donation form</button>
      </p>

      {askState && field('state', countryCode === 'US' ? 'State' : 'State / Province',
        <input type="text" autoComplete="address-level1" value={form.state} onChange={setField('state')} />)}

      {field('phone', 'Phone No.',
        <input type="tel" autoComplete="tel" value={form.phone} onChange={setField('phone')} />)}

      {field('email', 'Email',
        <input type="email" autoComplete="email" value={form.email} onChange={setField('email')} />, true)}

      {sendError && <p className="odf-send-error" role="alert">{sendError}</p>}

      <button type="submit" className="odf-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send the instructions to my Email now'}
      </button>
      <p className="odf-terms">
        By submitting I agree to the{' '}
        <TermsLink onOpen={openTerms}>terms and conditions</TermsLink>
      </p>
      {termsDialog}
    </form>
  );
}
